import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInAnonymously,
  signOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth, googleProvider, db } from '../../sdk_initialization';
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { clearOtherSessionPendingReservations } from '../services/reservationsService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const INACTIVE_ACCOUNT_MESSAGE = 'Your account is inactive. Please reach out to admin@icnacon.org.';
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionIdRef = useRef(null);
  const unsubscribeSessionRef = useRef(null);
  const takeoverNotifiedRef = useRef(false);

  // Sign up with email and password
  const signup = (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const enforceActiveUserStatus = async (user) => {
    if (!user?.uid) return;
    try {
      const profileRef = doc(db, 'profiles', user.uid);
      const profileSnap = await getDoc(profileRef);
      if (!profileSnap.exists()) return;

      const profile = profileSnap.data() || {};
      const status = String(profile.userStatus || '').trim().toLowerCase();
      if (status && status !== 'active') {
        try { sessionStorage.setItem('authMessage', INACTIVE_ACCOUNT_MESSAGE); } catch {}
        try { await signOut(auth); } catch {}
        throw new Error(INACTIVE_ACCOUNT_MESSAGE);
      }
    } catch (e) {
      // If reading profile fails due to permissions, do not block login here.
      if (e?.code === 'permission-denied') return;
      throw e;
    }
  };

  // Sign in with email and password
  const signin = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await enforceActiveUserStatus(cred?.user);
    return cred;
  };

  // Sign in with Google
  const signinWithGoogle = async () => {
    const cred = await signInWithPopup(auth, googleProvider);
    await enforceActiveUserStatus(cred?.user);
    return cred;
  };

  // Sign in anonymously
  const signinAnonymously = () => {
    return signInAnonymously(auth);
  };

  // Sign out
  const logout = () => {
    return signOut(auth);
  };

  // Send password reset email
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Generate a per-tab session id and keep it stable for this tab only
  useEffect(() => {
    if (!sessionIdRef.current) {
      try {
        // Try to reuse from sessionStorage for this tab
        let sid = sessionStorage.getItem('globalSessionId');
        if (!sid) {
          sid = (crypto && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2);
          sessionStorage.setItem('globalSessionId', sid);
        }
        sessionIdRef.current = sid;
      } catch {
        sessionIdRef.current = (Math.random().toString(36).slice(2) + Date.now());
      }
    }
  }, []);

  // Enforce single active session per user
  useEffect(() => {
    // Cleanup any previous listener
    if (unsubscribeSessionRef.current) {
      try { unsubscribeSessionRef.current(); } catch {}
      unsubscribeSessionRef.current = null;
    }
    if (!currentUser?.uid || !db || !sessionIdRef.current) return;

    const uid = currentUser.uid;
    const sid = sessionIdRef.current;
    const ref = doc(db, 'sessionStates', uid);

    (async () => {
      let tookOver = false;
      // Best-effort read to determine if we are taking over; ignore permission-denied
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          if (data.activeSessionId && data.activeSessionId !== sid) {
            tookOver = true;
          }
        }
      } catch (e) {
        // Reading session state may fail if rules not yet deployed; continue to attempt write
        if (e && e.code !== 'permission-denied') {
          // ignore other transient errors
        }
      }
      // Try to claim ownership
      try {
        // Build display name from profile or auth displayName
        let displayName = currentUser.displayName || null;
        // If no displayName in auth, try to fetch from profile
        if (!displayName) {
          try {
            const profileRef = doc(db, 'profiles', uid);
            const profileSnap = await getDoc(profileRef);
            if (profileSnap.exists()) {
              const profile = profileSnap.data() || {};
              const first = (profile.firstName || '').trim();
              const last = (profile.lastName || '').trim();
              if (first || last) {
                displayName = [first, last].filter(Boolean).join(' ');
              }
            }
          } catch {}
        }
        
        await setDoc(ref, { 
          activeSessionId: sid, 
          userId: uid,
          email: currentUser.email || null,
          displayName: displayName || null,
          updatedAt: serverTimestamp() 
        }, { merge: true });
        // Only after an actual takeover, proactively clear Pending holds from prior sessions for this user
        if (tookOver) {
          try {
            await clearOtherSessionPendingReservations({ profileId: uid, newSessionId: sid });
          } catch {}
        }
        if (tookOver && !takeoverNotifiedRef.current) {
          takeoverNotifiedRef.current = true;
          setTimeout(() => {
            try { alert('You signed in here. Your previous session was signed out.'); } catch {}
          }, 0);
        }
      } catch (e) {
        // If we cannot write session state (e.g., rules not deployed), skip enforcement gracefully
        return;
      }

      // Subscribe to session state changes to detect being invalidated by another login
      try {
        unsubscribeSessionRef.current = onSnapshot(ref, (snap) => {
          const data = snap.data() || {};
          const active = data?.activeSessionId;
          if (active && active !== sid) {
            try { alert('Your session expired because you signed in elsewhere.'); } catch {}
            signOut(auth).catch(() => {});
          }
        }, (err) => {
          // Permission errors: disable enforcement quietly
          if (err && err.code === 'permission-denied') {
            if (unsubscribeSessionRef.current) {
              try { unsubscribeSessionRef.current(); } catch {}
              unsubscribeSessionRef.current = null;
            }
          }
        });
      } catch {
        // ignore
      }
    })();

    // Cleanup on unmount or user change
    return () => {
      if (unsubscribeSessionRef.current) {
        try { unsubscribeSessionRef.current(); } catch {}
        unsubscribeSessionRef.current = null;
      }
    };
  }, [currentUser?.uid]);

  const value = {
    currentUser,
    signup,
    signin,
    signinWithGoogle,
    signinAnonymously,
    logout,
    resetPassword,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};